import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { RESEARCH } from "../../data/content";
import { SectionHeading } from "../common";
import { Reveal } from "../Reveal";

// Four approved concepts; each card resolves to the exact existing THG research
// article via RESEARCH (single source of truth — no guessed or duplicate routes).
const CONCEPTS = [
    {
        topic: "Goodhart's Law in Operating Environments",
        text: "When a measure becomes a target, reported success can separate from operating reality. THG applies this established principle to live operating environments.",
    },
    {
        topic: "Execution Visibility",
        text: "Seeing what changed, what operators did and what still requires action between the plan and the reported result.",
    },
    {
        topic: "KPI Reinforcement Loops",
        text: "When distorted measures shape behavior, the resulting behavior can reinforce the distortion.",
    },
    {
        topic: "Operational Cognition",
        text: "How operators recognize, interpret, prioritize and act under real operating constraints.",
    },
];

export const ResearchBehind = () => (
    <section className="py-24 md:py-32 border-b border-thg-border" data-testid="research-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading
                index="04"
                overline="Research"
                title="Research Behind the Systems"
                text="StoreCentral, Control Shift and Sarge are applications of a defined operating discipline—not disconnected software experiments. Titan Haven Group's research examines how measurement, execution and decision-making separate from operating reality, then builds systems intended to preserve that connection."
            />
            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 border-t border-l border-thg-border">
                {CONCEPTS.map((c, i) => {
                    const article = RESEARCH.find((r) => r.topic === c.topic);
                    return (
                        <Reveal key={c.topic} delay={i * 0.06}>
                            <a
                                href={article?.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`research-card-${i}`}
                                className="group block h-full border-b border-r border-thg-border p-8 hover:bg-thg-surface transition-colors duration-300"
                            >
                                <h3 className="font-display text-lg font-medium text-white">{c.topic}</h3>
                                <p className="mt-3 text-sm leading-relaxed text-thg-secondary">{c.text}</p>
                                <span className="mt-4 inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase text-thg-tertiary group-hover:text-white transition-colors duration-300">
                                    {article?.title} <ArrowUpRight size={13} />
                                </span>
                            </a>
                        </Reveal>
                    );
                })}
            </div>
            <Reveal delay={0.1}>
                <p className="mt-8 text-xs leading-relaxed text-thg-tertiary max-w-3xl" data-testid="research-authorship-note">
                    THG research is company-authored operating work published through WarRoom Labs. It informs how
                    THG builds — it is not independent validation of product outcomes.
                </p>
            </Reveal>
            <Reveal delay={0.15}>
                <Link
                    to="/warroom"
                    data-testid="research-explore-link"
                    className="mt-8 inline-flex items-center gap-2 text-sm text-thg-secondary hover:text-white transition-colors duration-300"
                >
                    Explore all WarRoom research <ArrowRight size={15} />
                </Link>
            </Reveal>
        </div>
    </section>
);
